import React, { useMemo, useCallback, useContext } from 'react';
import { InputNumber, Empty } from 'antd';
import {
  SyncOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  GlobalOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { SbTable, SbButton, SbInput } from 'uiCore';
import { AuthContext } from 'State/AuthContext';
import {
  COMP_PAGE_SIZE,
  COMP_PAGE_SIZE_OPTIONS,
} from 'constants/compensation.constants';
import useCurrencySettings from './useCurrencySettings';
import {
  CompTableWrapperStyled,
  CurrencyHeaderCard,
  CurrencyStatsRow,
  CurrencyStat,
  CurrencyBadge,
  CurrencyActionButtons,
  LegalEntityCell,
  ConversionRateText,
  UpdatedByCell,
  UpdatedByEmail,
  UpdatedByDate,
  EditCellWrapper,
  EditIconButton,
  FxRateInput,
  OpYearInput,
  EmptyStateWrapper,
  TabContentWrapper,
} from './CompensationManagement.styles';

const CurrencySettingsTable = () => {
  const { email: contextEmail } = useContext(AuthContext);
  const email = contextEmail || localStorage.getItem('username') || '';

  const {
    legalEntities,
    hqCurrency,
    loading,
    editingRowId,
    editValues,
    savingRowId,
    handleEditStart,
    handleEditChange,
    handleEditSave,
    handleEditCancel,
    fetchCurrencySettings,
  } = useCurrencySettings(email);

  const isEditing = useCallback(
    (record) => (record.id ?? record.legal_entity) === editingRowId,
    [editingRowId],
  );

  const columns = useMemo(
    () => [
      {
        title: 'Legal Entity / Country',
        dataIndex: 'legal_entity',
        key: 'legal_entity',
        width: '22%',
        render: (text, record) => (
          <LegalEntityCell>
            <span className="legal-entity-name">{text || '-'}</span>
            <span className="legal-entity-country">{record?.country || ''}</span>
          </LegalEntityCell>
        ),
      },
      {
        title: 'HQ Currency',
        key: 'hq_currency',
        align: 'center',
        width: '10%',
        filterValue: () => hqCurrency || 'USD',
        render: () => hqCurrency || 'USD',
      },
      {
        title: 'Local Currency',
        dataIndex: 'local_currency',
        key: 'local_currency',
        align: 'center',
        width: '10%',
        render: (text) => text || '-',
      },
      {
        title: `Conversion Ratio (1 ${hqCurrency} = N Local)`,
        dataIndex: 'fx_hq_to_local',
        key: 'fx_hq_to_local',
        width: '20%',
        filterLabel: (rate) => Number(rate).toFixed(4),
        render: (rate, record) => {
          if (isEditing(record)) {
            return (
              <EditCellWrapper>
                <FxRateInput>
                  <InputNumber
                    value={editValues?.fx_hq_to_local}
                    onChange={(val) => handleEditChange('fx_hq_to_local', val)}
                    min={0}
                    step={0.0001}
                    precision={4}
                    size="small"
                  />
                </FxRateInput>
              </EditCellWrapper>
            );
          }
          const formattedRate = rate != null ? Number(rate).toFixed(4) : '—';
          return (
            <ConversionRateText>
              <strong>{formattedRate}</strong>
            </ConversionRateText>
          );
        },
      },
      {
        title: 'Op Year',
        dataIndex: 'fx_operational_year',
        key: 'fx_operational_year',
        width: '10%',
        render: (text, record) => {
          if (isEditing(record)) {
            return (
              <OpYearInput>
                <SbInput
                  value={editValues?.fx_operational_year}
                  onChange={(e) => handleEditChange('fx_operational_year', e.target.value)}
                  size="small"
                />
              </OpYearInput>
            );
          }
          return text || '-';
        },
      },
      {
        title: 'Updated By',
        dataIndex: 'fx_updated_by',
        key: 'fx_updated_by',
        width: '20%',
        render: (text, record) => {
          const updatedEmail = text || '-';
          let formattedDate = '';
          if (record?.fx_last_updated) {
            try {
              formattedDate = new Date(record.fx_last_updated).toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              });
            } catch {
              formattedDate = record.fx_last_updated;
            }
          }
          return (
            <UpdatedByCell>
              <UpdatedByEmail>{updatedEmail}</UpdatedByEmail>
              {formattedDate && <UpdatedByDate>{formattedDate}</UpdatedByDate>}
            </UpdatedByCell>
          );
        },
      },
      {
        title: 'Action',
        key: 'action',
        align: 'center',
        width: '8%',
        render: (_, record) => {
          const rowKey = record.id ?? record.legal_entity;
          if (isEditing(record)) {
            const isSaving = savingRowId === rowKey;
            return (
              <EditCellWrapper>
                <SbButton
                  buttonType="primary"
                  size="small"
                  icon={<SaveOutlined />}
                  onClick={() => handleEditSave(record)}
                  loading={isSaving}
                  disabled={isSaving}
                />
                <SbButton
                  buttonType="secondary"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={handleEditCancel}
                  disabled={isSaving}
                />
              </EditCellWrapper>
            );
          }
          return (
            <EditIconButton
              type="button"
              title="Edit FX rate"
              aria-label="Edit FX rate"
              onClick={() => handleEditStart(record)}
            >
              <EditOutlined />
            </EditIconButton>
          );
        },
      },
    ],
    [hqCurrency, editingRowId, editValues, savingRowId, isEditing, handleEditStart, handleEditChange, handleEditSave, handleEditCancel],
  );

  if (!loading && legalEntities.length === 0) {
    return (
      <EmptyStateWrapper>
        <Empty description="No legal entities configured yet" />
      </EmptyStateWrapper>
    );
  }

  return (
    <TabContentWrapper>
      <CurrencyHeaderCard>
        <CurrencyStatsRow>
          <CurrencyStat>
            <GlobalOutlined />
            HQ Currency: <CurrencyBadge>{hqCurrency}</CurrencyBadge>
          </CurrencyStat>
          <CurrencyStat>
            <TeamOutlined />
            Entities: <strong>{legalEntities.length}</strong>
          </CurrencyStat>
        </CurrencyStatsRow>
        <CurrencyActionButtons>
          <SbButton
            buttonType="secondary"
            size="small"
            icon={<SyncOutlined />}
            onClick={fetchCurrencySettings}
            loading={loading}
          >
            Refresh
          </SbButton>
        </CurrencyActionButtons>
      </CurrencyHeaderCard>

      <CompTableWrapperStyled>
        <SbTable
          columns={columns}
          dataSource={legalEntities}
          loading={loading}
          rowKey={(record) => record.id ?? record.legal_entity}
          rowClassName={(record) => (isEditing(record) ? 'comp-editing-row' : '')}
          pagination={{
            defaultPageSize: COMP_PAGE_SIZE,
            pageSizeOptions: COMP_PAGE_SIZE_OPTIONS,
            showSizeChanger: true,
            size: 'small',
          }}
          scroll={{ y: 'calc(100vh - 355px)' }}
          sticky
        />
      </CompTableWrapperStyled>
    </TabContentWrapper>
  );
};

export default CurrencySettingsTable;
